/**
 * The guides list's state, as it lives in the URL and as it travels to the BFF.
 *
 * In the URL so a filtered view can be bookmarked and shared, and so Back after opening a guide
 * returns to the same page of the same filter rather than to the top of everything. The keys
 * follow the console's convention (`page`, `filter_*`) and, per CLAUDE.md, never change once
 * shipped: a renamed key turns a saved URL into a silently different result set.
 */

export const GUIDE_PAGE_SIZE = 50;

/** Every status a guide row can carry, in the order the chips show them. */
export const STATUS_FILTERS = [
  { value: 'ACTIVE', label: 'Published' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'IMPORTING', label: 'Importing' },
  { value: 'IMPORT_FAILED', label: 'Import failed' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

export const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  STATUS_FILTERS.map((s) => [s.value, s.label]),
);

export const TYPE_FILTERS = [
  { value: 'skill-guide', label: 'Academy' },
  { value: 'hiring-guide', label: 'Hiring' },
  { value: 'career-guide', label: 'Career' },
] as const;

export const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  TYPE_FILTERS.map((t) => [t.value, t.label]),
);

/** '' is the backend default: newest published first. */
export const SORT_OPTIONS = [
  { value: '', label: 'Newest published' },
  { value: 'updatedAt', label: 'Recently edited' },
  { value: 'viewCount', label: 'Most viewed' },
  { value: 'title', label: 'Title A to Z' },
] as const;

export interface GuideListState {
  /** 0-based, like every other paged list in the console. */
  page: number;
  statuses: string[];
  /** '' for all types. */
  pageType: string;
  q: string;
  sort: string;
}

export const EMPTY_GUIDE_LIST_STATE: GuideListState = {
  page: 0,
  statuses: [],
  pageType: '',
  q: '',
  sort: '',
};

const KNOWN_STATUSES: string[] = STATUS_FILTERS.map((s) => s.value);
const KNOWN_TYPES: string[] = TYPE_FILTERS.map((t) => t.value);
const KNOWN_SORTS: string[] = SORT_OPTIONS.map((s) => s.value);

/** URL -> state. Anything unrecognised is dropped rather than sent: it is a stale link. */
export function parseGuideListParams(params: URLSearchParams): GuideListState {
  const page = Number.parseInt(params.get('page') ?? '0', 10);
  const statuses = (params.get('filter_status') ?? '')
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => KNOWN_STATUSES.includes(s));
  const pageType = params.get('filter_pageType') ?? '';
  const sort = params.get('sort') ?? '';
  return {
    page: Number.isFinite(page) && page >= 0 ? page : 0,
    statuses: KNOWN_STATUSES.filter((s) => statuses.includes(s)),
    pageType: KNOWN_TYPES.includes(pageType) ? pageType : '',
    q: (params.get('q') ?? '').slice(0, 120),
    sort: KNOWN_SORTS.includes(sort) ? sort : '',
  };
}

/** State -> URL, omitting defaults so an unfiltered list is a clean /guides. */
export function toGuideListParams(state: GuideListState): URLSearchParams {
  const p = new URLSearchParams();
  if (state.statuses.length) p.set('filter_status', state.statuses.join(','));
  if (state.pageType) p.set('filter_pageType', state.pageType);
  if (state.q.trim()) p.set('q', state.q.trim());
  if (state.sort) p.set('sort', state.sort);
  if (state.page > 0) p.set('page', String(state.page));
  return p;
}

/** State -> GET /api/guides query. */
export function toGuideApiParams(state: GuideListState): Record<string, string> {
  const out: Record<string, string> = {
    page: String(state.page),
    size: String(GUIDE_PAGE_SIZE),
  };
  if (state.statuses.length) out.status = state.statuses.join(',');
  if (state.pageType) out.pageType = state.pageType;
  if (state.q.trim()) out.q = state.q.trim();
  if (state.sort) out.sort = state.sort;
  return out;
}

/** Toggle one status chip. A filter change always returns to the first page. */
export function toggleStatus(state: GuideListState, status: string): GuideListState {
  const on = state.statuses.includes(status);
  const statuses = on
    ? state.statuses.filter((s) => s !== status)
    : KNOWN_STATUSES.filter((s) => s === status || state.statuses.includes(s));
  return { ...state, statuses, page: 0 };
}

/** One guide's outcome from POST /api/guides/bulk-status. */
export interface BulkStatusResult {
  _id: string;
  title?: string;
  slug?: string;
  status?: string;
  ok: boolean;
  unchanged?: boolean;
  problems?: string[];
}

export interface BulkStatusResponse {
  status: 'ACTIVE' | 'DRAFT';
  requested: number;
  changed: number;
  updated: number;
  results: BulkStatusResult[];
}

export interface BulkSummary {
  /** "12 published, 3 need fixes" */
  headline: string;
  severity: 'success' | 'warning' | 'error';
  failures: { _id: string; title: string; problem: string }[];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Turn the per-guide results into the sentence and the list the page shows. */
export function summariseBulkResult(r: BulkStatusResponse): BulkSummary {
  const publishing = r.status === 'ACTIVE';
  const done = r.results.filter((x) => x.ok && !x.unchanged).length;
  const already = r.results.filter((x) => x.ok && x.unchanged).length;
  const failures = r.results
    .filter((x) => !x.ok)
    .map((x) => ({
      _id: x._id,
      title: x.title || x.slug || x._id,
      problem: x.problems?.[0] ?? 'refused without a reason',
    }));

  const parts = [`${done} ${publishing ? 'published' : 'unpublished'}`];
  if (already > 0) {
    parts.push(`${already} already ${publishing ? 'live' : 'not live'}`);
  }
  if (failures.length > 0) {
    parts.push(
      publishing
        ? `${plural(failures.length, 'needs', 'need')} fixes`
        : `${failures.length} could not be changed`,
    );
  }
  return {
    headline: parts.join(', '),
    severity: failures.length === 0 ? 'success' : done > 0 ? 'warning' : 'error',
    failures,
  };
}
